import { Link } from 'react-router-dom'
import { Loader } from '../../../shared/ui/Loader'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { useRosDrilldown } from './useRosDashboard'
import type { RosDashboardFilters, RosDrillKind, RosPeriodKey } from './types'

// Модалка-список позиций за период (16.07): «Ушло в счёт» / «Ушло в отказ» /
// «Проквотировано». В очередь эти клики не ведут — у неё нет понятия периода,
// а позиции отказных заявок (CLOSED_LOST) в ней вообще не видны.

const TITLES: Record<RosDrillKind, string> = {
  invoice: 'Ушло в счёт за период',
  refusal: 'Ушло в отказ за период',
  quoted: 'Проквотировано за период',
}

export function RosDrilldownModal({
  kind,
  period,
  filters,
  onClose,
}: {
  kind: RosDrillKind
  period: RosPeriodKey
  filters: RosDashboardFilters
  onClose: () => void
}) {
  const { data, isLoading, error } = useRosDrilldown(kind, period, filters)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[80vh] flex flex-col rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">{TITLES[kind]}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        {isLoading && (
          <div className="py-10 flex justify-center">
            <Loader variant="ring" size={28} />
          </div>
        )}
        {!isLoading && error != null && <ErrorMessage error={error} />}
        {!isLoading && !error && data && (
          <>
            <div className="text-xs text-gray-500 mb-2">
              {data.total.toLocaleString('ru-RU')} позиций
              {data.total > data.items.length && ` · показаны первые ${data.items.length}`}
            </div>
            {data.items.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">За период ничего нет</div>
            ) : (
              // На телефоне таблица шире окна — прокрутка по горизонтали, а не
              // сжатые колонки с переломанным текстом.
              <div className="flex-1 overflow-auto -mx-1 px-1">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3 font-medium">Артикул</th>
                      <th className="py-2 px-3 font-medium">Бренд</th>
                      <th className="py-2 px-3 font-medium">Клиент</th>
                      <th className="py-2 px-3 font-medium">Заявка</th>
                      <th className="py-2 px-3 font-medium">Закупщик</th>
                      <th className="py-2 pl-3 font-medium text-right">Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((it) => (
                      <tr key={it.requestItemId} className="border-b border-gray-100 last:border-0">
                        <td className="py-1.5 pr-3">
                          <Link
                            to={`/request-items/${it.requestItemId}`}
                            className="font-mono text-brand-600 hover:text-brand-700 whitespace-nowrap"
                          >
                            {it.partNumber}
                          </Link>
                        </td>
                        <td className="py-1.5 px-3 text-gray-700">{it.brand ?? '—'}</td>
                        <td className="py-1.5 px-3 text-gray-700 truncate max-w-[180px]">
                          {it.clientName}
                        </td>
                        <td className="py-1.5 px-3">
                          <Link
                            to={`/requests/${it.requestId}`}
                            className="text-brand-600 hover:text-brand-700 font-mono whitespace-nowrap"
                          >
                            {it.requestNumber ?? '—'}
                          </Link>
                        </td>
                        <td className="py-1.5 px-3 text-gray-700">{it.buyerName ?? '—'}</td>
                        <td className="py-1.5 pl-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                          {new Date(it.eventAt).toLocaleDateString('ru-RU')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
